import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Users, Calendar, ArrowUpRight, Package, Check, X, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDateFr } from "../lib/dateFormat";
import { formatTimezoneLabel, timezoneFull } from "../lib/timezone";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import SignaturePad from "../components/SignaturePad";
import TodayShiftsPointage from "../components/TodayShiftsPointage";
import MyUpcomingShifts from "../components/MyUpcomingShifts";
import SiteReceptionsToConfirm from "../components/SiteReceptionsToConfirm";
import SitePickupsToSign from "../components/SitePickupsToSign";
import LateArrivalsWidget from "../components/LateArrivalsWidget";
import OverdueReturnsWidget from "../components/OverdueReturnsWidget";
import AbsentCollaboratorsWidget from "../components/AbsentCollaboratorsWidget";
import VacantInterventionsWidget from "../components/VacantInterventionsWidget";
import DemandesSitesDashboardWidget from "../components/DemandesSitesDashboardWidget";
import MyReferralCodesWidget from "../components/MyReferralCodesWidget";
import StickyPageHeader from "../components/StickyPageHeader";

const KPI = ({ label, value, hint, icon: Icon, testid, tint = "#0055A4" }) => (
  <div
    className="bg-white border border-zinc-200 p-5 rounded-sm hover:border-zinc-300 transition-colors"
    data-testid={testid}
  >
    <div className="flex items-start justify-between mb-4">
      <p className="label-uppercase">{label}</p>
      <span
        className="h-9 w-9 inline-flex items-center justify-center rounded-sm"
        style={{ backgroundColor: `${tint}14`, color: tint }}
      >
        <Icon size={18} strokeWidth={1.75} />
      </span>
    </div>
    <p className="font-display text-3xl font-bold tracking-tight text-zinc-900">{value}</p>
    <p className="text-xs text-zinc-500 mt-2 font-mono">{hint}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { selectedBrandId, brands } = useBrands();
  const [stats, setStats] = useState(null);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [recentShifts, setRecentShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [leaveCategories, setLeaveCategories] = useState([]);
  const [equipRequests, setEquipRequests] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
        const [statsRes, leavesRes, shiftsRes, usersRes, catsRes, equipRes, entriesRes] = await Promise.all([
          api.get("/dashboard/stats", { params }),
          api.get("/leaves", { params: { status: "en_attente", ...(selectedBrandId ? { brand_id: selectedBrandId } : {}) } }),
          api.get("/shifts", { params }),
          api.get("/collaborateurs", { params }),
          api.get("/unavailability-categories"),
          api.get("/equipment-requests", { params: { status: "en_attente", ...(selectedBrandId ? { brand_id: selectedBrandId } : {}) } }),
          api.get("/timeentries", { params }),
        ]);
        setStats(statsRes.data);
        setRecentLeaves(leavesRes.data?.slice(0, 5) || []);
        setUsers(usersRes.data || []);
        setLeaveCategories(catsRes.data || []);
        setEquipRequests((equipRes.data || []).slice(0, 5));

        // "Derniers shifts" — filtrer uniquement les shifts PASSÉS et POINTÉS (clock_in + clock_out)
        const entries = entriesRes.data || [];
        const completedShiftIds = new Set();
        const completedByCollabDate = new Set();
        for (const e of entries) {
          if (e.clock_in && e.clock_out) {
            if (e.shift_id) {
              completedShiftIds.add(e.shift_id);
            } else if (e.collaborateur_id && e.date) {
              // Fallback pour les pointages historiques SANS shift_id : matching par (collab, date).
              // Restreint volontairement aux entries sans shift_id afin d'éviter de faire remonter
              // par erreur un second shift non pointé sur la même journée.
              completedByCollabDate.add(`${e.collaborateur_id}__${e.date}`);
            }
          }
        }
        const now = new Date();
        const allShifts = shiftsRes.data || [];
        const pastCompleted = allShifts
          .filter((s) => {
            if (!s.date || !s.end_time) return false;
            // Le shift doit être totalement terminé (date + heure de fin écoulées, heure locale)
            const endDate = new Date(`${s.date}T${s.end_time}:00`);
            if (Number.isNaN(endDate.getTime()) || endDate >= now) return false;
            // Pointage entrée + sortie effectués
            if (completedShiftIds.has(s.id)) return true;
            return completedByCollabDate.has(`${s.collaborateur_id}__${s.date}`);
          })
          .sort((a, b) => {
            const ka = `${b.date}T${b.end_time || "00:00"}`;
            const kb = `${a.date}T${a.end_time || "00:00"}`;
            return ka.localeCompare(kb);
          })
          .slice(0, 6);
        setRecentShifts(pastCompleted);
      } catch (e) {
        // ignore
      }
    };
    load();

    // Rafraîchissement temps réel : recharge la liste des demandes matériel en attente
    // dès qu'une notification de type 'equipment_request' arrive via SSE.
    const refreshEquipRequests = async () => {
      try {
        const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
        const res = await api.get("/equipment-requests", {
          params: { status: "en_attente", ...params },
        });
        setEquipRequests((res.data || []).slice(0, 5));
      } catch { /* ignore */ }
    };
    const onNotif = (ev) => {
      const n = ev?.detail;
      if (!n) return;
      if (n.type === "equipment_request" || n.type === "equipment_request_decision") {
        refreshEquipRequests();
      }
    };
    // Rafraîchissement KPI (stats) lorsqu'un shift est créé/modifié/supprimé
    const refreshStats = async () => {
      try {
        const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
        const res = await api.get("/dashboard/stats", { params });
        setStats(res.data);
      } catch { /* ignore */ }
    };
    const onShiftsUpdated = () => { refreshStats(); };
    window.addEventListener("plania:notification", onNotif);
    window.addEventListener("plania:shifts-updated", onShiftsUpdated);
    // Filet de sécurité : poll basse fréquence (60s) au cas où le SSE est coupé.
    const pollId = setInterval(() => { refreshEquipRequests(); refreshStats(); }, 60000);
    return () => {
      window.removeEventListener("plania:notification", onNotif);
      window.removeEventListener("plania:shifts-updated", onShiftsUpdated);
      clearInterval(pollId);
    };
  }, [selectedBrandId]);

  const brandLabel = selectedBrandId
    ? brands.find((b) => b.id === selectedBrandId)?.name || "—"
    : "Tous les départements";

  const isCollab = user?.role === "collaborateur";
  const isGestOrCollab = user?.role === "gestionnaire" || isCollab;
  const canDecideEquip = user?.role === "admin" || user?.role === "manager";

  // Décision (validation/refus) d'une demande matériel — modale signature
  const [decisionReq, setDecisionReq] = useState(null);
  const [decisionMode, setDecisionMode] = useState("approuve"); // approuve|refuse
  const [decisionForm, setDecisionForm] = useState({ controller_signature: null, quantite_validee: 1, decision_note: "" });

  const openDecision = (req, mode) => {
    setDecisionReq(req);
    setDecisionMode(mode);
    setDecisionForm({
      controller_signature: null,
      quantite_validee: req.quantite_demandee || 1,
      decision_note: "",
    });
  };

  const submitDecision = async () => {
    if (!decisionReq) return;
    if (decisionMode === "approuve") {
      if (!decisionForm.controller_signature) {
        toast.error("Veuillez signer pour valider la demande");
        return;
      }
      const qty = Number(decisionForm.quantite_validee);
      if (!qty || qty < 1) {
        toast.error("Quantité validée invalide");
        return;
      }
    }
    try {
      await api.put(`/equipment-requests/${decisionReq.id}/decision`, {
        status: decisionMode,
        controller_signature: decisionMode === "approuve" ? decisionForm.controller_signature : null,
        quantite_validee: decisionMode === "approuve" ? Number(decisionForm.quantite_validee) : null,
        decision_note: decisionForm.decision_note || "",
      });
      toast.success(
        decisionMode === "approuve"
          ? "Demande approuvée — nouvelle affectation créée"
          : "Demande refusée"
      );
      setEquipRequests((prev) => prev.filter((r) => r.id !== decisionReq.id));
      setDecisionReq(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <StickyPageHeader testid="dashboard-sticky-header">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Vue d&apos;ensemble</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900" data-testid="dashboard-title">
            Tableau de bord
          </h1>
          {!isCollab && (
            <p className="text-sm text-zinc-600 mt-2 font-mono">Section · {brandLabel}</p>
          )}
        </div>
      </div>
      </StickyPageHeader>

      {/* KPI Grid */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isCollab ? "lg:grid-cols-2" : "lg:grid-cols-3"} gap-4`}>
        {!isCollab && (
          <KPI
            label="Collaborateurs actifs"
            value={stats?.total_collaborateurs ?? "—"}
            hint="Effectif total"
            icon={Users}
            tint="#DB2777"
            testid="kpi-collaborateurs"
          />
        )}
        <KPI
          label="Shifts cette semaine"
          value={stats?.shifts_this_week ?? "—"}
          hint="Planifiés"
          icon={Calendar}
          tint="#4F46E5"
          testid="kpi-shifts-week"
        />
        <KPI
          label="Shifts ce mois"
          value={stats?.shifts_this_month ?? "—"}
          hint="Interventions planifiées"
          icon={Calendar}
          tint="#7C3AED"
          testid="kpi-shifts-month"
        />
      </div>

      {/* Pointage du jour — collab/gestionnaire voient leurs shifts à venir (semaine/mois) ; admin/manager voient la vue pilotage du jour */}
      {(user?.role === "collaborateur" || user?.role === "gestionnaire") ? (
        <MyUpcomingShifts />
      ) : (
        <TodayShiftsPointage />
      )}

      {/* Mon code parrainage — tous rôles hors partenaire (le rôle partenaire n'atteint pas ce dashboard) */}
      <MyReferralCodesWidget />

      {/* Matériels sites à réceptionner — tous rôles (visible uniquement si désigné receiver) */}
      <div data-testid="dashboard-site-receptions-section">
        <SiteReceptionsToConfirm />
      </div>

      {/* Enlèvements sites à retirer — tous rôles (visible uniquement si désigné référent enlèvement) */}
      <div data-testid="dashboard-site-pickups-section">
        <SitePickupsToSign />
      </div>

      {/* Demandes sites — collab / gestionnaire uniquement (hors admin & manager) */}
      {(user?.role === "collaborateur" || user?.role === "gestionnaire") && (
        <DemandesSitesDashboardWidget />
      )}

      {/* Retards de pointage — réservé admin / manager */}
      {(user?.role === "admin" || user?.role === "manager") && <LateArrivalsWidget />}

      {/* Retards de restitution matériel — réservé admin / manager */}
      {(user?.role === "admin" || user?.role === "manager") && <OverdueReturnsWidget />}

      {/* Interventions à recombler & Collaborateurs absents — admin / manager */}
      {(user?.role === "admin" || user?.role === "manager") && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <VacantInterventionsWidget />
          <AbsentCollaboratorsWidget />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent leaves */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 p-6 rounded-sm">
          <div className="mb-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="label-uppercase mb-1" data-testid="dashboard-pending-label">
                  {isGestOrCollab ? "Mes demandes" : "En attente de validation"}
                </p>
                <h2 className="font-display font-semibold text-xl text-zinc-900">
                  {isGestOrCollab ? "Mes demandes en cours de traitement par l'entreprise" : "Demandes en attente"}
                </h2>
                {(() => {
                  const totalPending = (stats?.leaves_pending || 0) + equipRequests.length;
                  if (totalPending === 0) return null;
                  return (
                    <p className="text-xs text-zinc-500 font-mono mt-1" data-testid="pending-count">
                      {totalPending} demande{totalPending > 1 ? "s" : ""} à valider
                    </p>
                  );
                })()}
              </div>
              <Link to="/conges" className="text-sm text-[#0055FF] hover:underline flex items-center gap-1 shrink-0" data-testid="see-leaves-link">
                Voir tout <ArrowUpRight size={14} />
              </Link>
            </div>
            {isGestOrCollab && (
              <div className="mt-3">
                <Link to="/conges?new=1" data-testid="mes-demandes-quick-new-btn">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-[#0055FF] hover:bg-[#0044CC]"
                  >
                    <Plus size={13} className="mr-1.5" /> Nouvelle demande d'indisponibilité
                  </Button>
                </Link>
              </div>
            )}
          </div>
          {recentLeaves.length === 0 && equipRequests.length === 0 ? (
            <p className="text-sm text-zinc-500 font-mono py-6">Aucune demande en attente</p>
          ) : (
            <div className="divide-y divide-zinc-100">
              {recentLeaves.map((l) => {
                const u = users.find((x) => x.id === l.collaborateur_id);
                const cat = leaveCategories.find((c) => c.id === l.category_id);
                return (
                  <div key={l.id} className="py-3 flex items-center justify-between gap-3" data-testid={`leave-row-${l.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-violet-50 border-violet-200 text-violet-800">
                          Indispo
                        </span>
                        <p className="text-sm font-medium text-zinc-900">{u?.name || "Collaborateur"}</p>
                        {cat && (
                          <span
                            className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider"
                            style={{
                              backgroundColor: `${cat.color || "#71717A"}14`,
                              borderColor: `${cat.color || "#71717A"}55`,
                              color: cat.color || "#71717A",
                            }}
                            data-testid={`leave-category-${l.id}`}
                          >
                            {cat.name}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 font-mono mt-0.5">
                        {formatDateFr(l.start_date)} → {formatDateFr(l.end_date)}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase font-semibold px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm tracking-wider shrink-0">
                      En attente
                    </span>
                  </div>
                );
              })}
              {equipRequests.map((r) => {
                const u = users.find((x) => x.id === r.requester_id);
                const reasonLabel = ({
                  usure: "Usure", defaut: "Défaut", perte: "Perte", autre: "Autre",
                })[r.reason_type] || r.reason_type;
                return (
                  <div key={r.id} className="py-3 flex items-center justify-between gap-3" data-testid={`equip-req-row-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-teal-50 border-teal-200 text-teal-800">
                          <Package size={10} /> Matériel
                        </span>
                        <p className="text-sm font-medium text-zinc-900">
                          {u?.name || r.requester_name || "Collaborateur"}
                        </p>
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-700">
                          {reasonLabel}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 font-mono mt-0.5 truncate">
                        {r.designation_snapshot || "—"}
                        {r.ref_id_snapshot ? ` · ${r.ref_id_snapshot}` : ""}
                        {r.quantite_demandee ? ` · Qté demandée : ${r.quantite_demandee}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] uppercase font-semibold px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm tracking-wider">
                        En attente
                      </span>
                      {canDecideEquip && (
                        <>
                          <button
                            type="button"
                            onClick={() => openDecision(r, "approuve")}
                            className="h-7 w-7 inline-flex items-center justify-center border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-sm"
                            data-testid={`equip-req-approve-${r.id}`}
                            title="Approuver"
                          >
                            <Check size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openDecision(r, "refuse")}
                            className="h-7 w-7 inline-flex items-center justify-center border border-red-200 text-red-700 hover:bg-red-50 rounded-sm"
                            data-testid={`equip-req-refuse-${r.id}`}
                            title="Refuser"
                          >
                            <X size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent shifts */}
        <div className="bg-white border border-zinc-200 p-6 rounded-sm">
          <p className="label-uppercase mb-1">Activité</p>
          <h2 className="font-display font-semibold text-xl text-zinc-900 mb-5">Derniers shifts</h2>
          <p className="text-[11px] font-mono text-zinc-500 -mt-4 mb-5">
            Shifts terminés et pointés (entrée + sortie)
          </p>
          {recentShifts.length === 0 ? (
            <p className="text-sm text-zinc-500 font-mono">Aucun shift terminé et pointé pour le moment</p>
          ) : (
            <div className="space-y-3">
              {recentShifts.map((s) => {
                const u = users.find((x) => x.id === s.collaborateur_id);
                const b = brands.find((x) => x.id === s.brand_id);
                return (
                  <div key={s.id} className="flex items-center gap-3 pb-3 border-b border-zinc-100 last:border-0" data-testid={`shift-${s.id}`}>
                    <span
                      className="h-8 w-1 rounded-sm"
                      style={{ backgroundColor: b?.color || "#0055FF" }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{u?.name || "—"}</p>
                      <p className="text-xs text-zinc-500 font-mono">
                        {formatDateFr(s.date)} · {s.start_time}–{s.end_time}
                        {b?.timezone && (() => {
                          const tz = formatTimezoneLabel(b.timezone);
                          return tz.full ? (
                            <span
                              className="ml-2 text-zinc-400"
                              title={timezoneFull(b.timezone)}
                              data-testid={`dashboard-shift-tz-${s.id}`}
                            >
                              · {tz.full}
                            </span>
                          ) : null;
                        })()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modale décision demande matériel (signature contrôleur obligatoire pour validation) */}
      <Dialog open={!!decisionReq} onOpenChange={(v) => { if (!v) setDecisionReq(null); }}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg" data-testid="equip-decision-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>
              {decisionMode === "approuve" ? "Valider la demande de matériel" : "Refuser la demande de matériel"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {decisionMode === "approuve"
                ? "Validation d'une demande de remplacement — signature contrôleur obligatoire. Une nouvelle affectation sera automatiquement créée, en attente de confirmation par le collaborateur."
                : "Refus d'une demande de remplacement — précisez le motif si nécessaire."}
            </DialogDescription>
          </DialogHeader>
          {decisionReq && (
            <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-sm space-y-1">
                <p>
                  <b>{decisionReq.designation_snapshot || "—"}</b>
                  <span className="text-xs font-mono text-zinc-600 ml-2">({decisionReq.ref_id_snapshot || "—"})</span>
                </p>
                <p className="text-xs font-mono text-zinc-600">
                  Demandeur : {decisionReq.requester_name || "—"} · Qté demandée : <b>{decisionReq.quantite_demandee || 1}</b>
                </p>
                <p className="text-xs font-mono text-zinc-600">
                  Motif : {decisionReq.reason_type}{decisionReq.reason ? ` — ${decisionReq.reason}` : ""}
                </p>
              </div>

              {decisionMode === "approuve" && (
                <>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Quantité validée <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="number" min="1"
                      value={decisionForm.quantite_validee}
                      onChange={(e) => setDecisionForm({ ...decisionForm, quantite_validee: e.target.value })}
                      data-testid="decision-qty-input"
                    />
                    <p className="text-[11px] text-zinc-500 font-mono mt-1">
                      Le stock disponible sera vérifié. Une nouvelle affectation sera créée pour {decisionReq.requester_name || "le collaborateur"}.
                    </p>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Signature contrôleur ({user?.name}) <span className="text-red-600">*</span>
                    </Label>
                    <SignaturePad
                      onSave={(url) => setDecisionForm((f) => ({ ...f, controller_signature: url }))}
                      value={decisionForm.controller_signature}
                    />
                    <p className="text-[11px] text-zinc-500 font-mono mt-1">
                      Le collaborateur devra ensuite signer la réception du nouveau matériel depuis Mon profil.
                    </p>
                  </div>
                </>
              )}

              <div>
                <Label className="label-uppercase mb-1.5 block">
                  Note de décision {decisionMode === "refuse" ? <span className="text-zinc-400">(recommandé)</span> : <span className="text-zinc-400">(optionnel)</span>}
                </Label>
                <Textarea
                  rows={3}
                  value={decisionForm.decision_note}
                  onChange={(e) => setDecisionForm({ ...decisionForm, decision_note: e.target.value })}
                  placeholder={decisionMode === "refuse" ? "Précisez la raison du refus…" : "Commentaire interne…"}
                  data-testid="decision-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setDecisionReq(null)}>Annuler</Button>
            <Button
              onClick={submitDecision}
              disabled={decisionMode === "approuve" && !decisionForm.controller_signature}
              className={decisionMode === "approuve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="decision-submit-btn"
            >
              {decisionMode === "approuve" ? "Valider la demande" : "Confirmer le refus"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
